import React, { useRef, useEffect, useState, useCallback } from 'react';
import Typography from '../Text/Typography';
import './Popover.css';

interface PopoverOption {
  id: string;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface PopoverProps {
  options: PopoverOption[];
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Horizontal alignment against the trigger. Prefer end for right-side avatars. */
  align?: 'start' | 'end';
  triggerRef?: React.RefObject<HTMLDivElement | null>;
}

type Placement =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'left-top'
  | 'left-bottom'
  | 'right-top'
  | 'right-bottom';

const Popover: React.FC<PopoverProps> = ({
  options,
  isOpen,
  onClose,
  className = '',
  position = 'bottom',
  align = 'start',
  triggerRef
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [currentPlacement, setCurrentPlacement] = useState<Placement>('bottom-left');
  const [isPositionReady, setIsPositionReady] = useState(false);
  const lockedPlacementRef = useRef<Placement | null>(null);

  const getFallbackPlacements = useCallback((preferredPosition: string, preferredAlign: string): Placement[] => {
    const endFirst = preferredAlign === 'end';
    switch (preferredPosition) {
      case 'top':
        return endFirst
          ? ['top-right', 'top-left', 'bottom-right', 'bottom-left']
          : ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
      case 'bottom':
        return endFirst
          ? ['bottom-right', 'bottom-left', 'top-right', 'top-left']
          : ['bottom-left', 'bottom-right', 'top-left', 'top-right'];
      case 'left':
        return ['left-top', 'left-bottom', 'right-top', 'right-bottom'];
      case 'right':
        return ['right-top', 'right-bottom', 'left-top', 'left-bottom'];
      default:
        return endFirst
          ? ['bottom-right', 'bottom-left', 'top-right', 'top-left']
          : ['bottom-left', 'bottom-right', 'top-left', 'top-right'];
    }
  }, []);

  const calculatePosition = useCallback((placement: Placement, triggerRect: DOMRect, popoverRect: DOMRect) => {
    const margin = 8;
    let top = 0;
    let left = 0;

    switch (placement) {
      case 'top-left':
        top = triggerRect.top - popoverRect.height - margin;
        left = triggerRect.left;
        break;
      case 'top-right':
        top = triggerRect.top - popoverRect.height - margin;
        left = triggerRect.right - popoverRect.width;
        break;
      case 'bottom-left':
        top = triggerRect.bottom + margin;
        left = triggerRect.left;
        break;
      case 'bottom-right':
        top = triggerRect.bottom + margin;
        left = triggerRect.right - popoverRect.width;
        break;
      case 'left-top':
        top = triggerRect.top;
        left = triggerRect.left - popoverRect.width - margin;
        break;
      case 'left-bottom':
        top = triggerRect.bottom - popoverRect.height;
        left = triggerRect.left - popoverRect.width - margin;
        break;
      case 'right-top':
        top = triggerRect.top;
        left = triggerRect.right + margin;
        break;
      case 'right-bottom':
        top = triggerRect.bottom - popoverRect.height;
        left = triggerRect.right + margin;
        break;
    }

    return { top, left };
  }, []);

  const isPlacementValid = useCallback((placement: Placement, triggerRect: DOMRect, popoverRect: DOMRect) => {
    const { top, left } = calculatePosition(placement, triggerRect, popoverRect);
    const margin = 8;

    return (
      top >= margin &&
      left >= margin &&
      top + popoverRect.height <= window.innerHeight - margin &&
      left + popoverRect.width <= window.innerWidth - margin
    );
  }, [calculatePosition]);

  const findBestPlacement = useCallback((triggerRect: DOMRect, popoverRect: DOMRect) => {
    if (lockedPlacementRef.current) return lockedPlacementRef.current;

    const fallbackPlacements = getFallbackPlacements(position, align);

    for (const placement of fallbackPlacements) {
      if (isPlacementValid(placement, triggerRect, popoverRect)) {
        return placement;
      }
    }

    return fallbackPlacements[0];
  }, [position, align, getFallbackPlacements, isPlacementValid]);

  const updatePosition = useCallback((lockPlacement = false) => {
    if (!isOpen || !triggerRef?.current || !popoverRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();

    if (popoverRect.width === 0 || popoverRect.height === 0) {
      requestAnimationFrame(() => updatePosition(lockPlacement));
      return;
    }

    const bestPlacement = findBestPlacement(triggerRect, popoverRect);
    if (lockPlacement) lockedPlacementRef.current = bestPlacement;

    const { top, left } = calculatePosition(bestPlacement, triggerRect, popoverRect);

    setCurrentPlacement(bestPlacement);
    setPopoverStyle({
      position: 'fixed',
      top: `${Math.max(8, Math.min(top, window.innerHeight - popoverRect.height - 8))}px`,
      left: `${Math.max(8, Math.min(left, window.innerWidth - popoverRect.width - 8))}px`,
      zIndex: 1001
    });
  }, [isOpen, triggerRef, findBestPlacement, calculatePosition]);

  useEffect(() => {
    if (!isOpen) {
      setIsPositionReady(false);
      lockedPlacementRef.current = null;
      setPopoverStyle({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && popoverStyle.top != null) {
      setIsPositionReady(true);
    }
  }, [isOpen, popoverStyle]);

  useEffect(() => {
    if (!isOpen || !popoverRef.current) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          updatePosition(true);
          resizeObserver.disconnect();
          break;
        }
      }
    });

    resizeObserver.observe(popoverRef.current);
    const fallbackTimer = window.setTimeout(() => {
      updatePosition(true);
      resizeObserver.disconnect();
    }, 50);

    return () => {
      resizeObserver.disconnect();
      window.clearTimeout(fallbackTimer);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Keep the chosen corner while open; only recompute coordinates against the trigger.
    const reanchor = () => updatePosition(false);

    window.addEventListener('resize', reanchor);
    window.addEventListener('scroll', reanchor, true);

    return () => {
      window.removeEventListener('resize', reanchor);
      window.removeEventListener('scroll', reanchor, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef?.current?.contains(target)) return;
      onClose?.();
    };

    // Capture phase so we close before any toggle handler races with this.
    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  const handleOptionClick = (option: PopoverOption) => {
    if (option.disabled) return;
    option.onClick?.();
    onClose?.();
  };

  return (
    <div className={`popover-overlay ${className}`}>
      <div
        ref={popoverRef}
        className="popover-container"
        style={{
          ...popoverStyle,
          visibility: isPositionReady ? 'visible' : 'hidden'
        }}
        data-placement={currentPlacement}
      >
        {options.map((option) => (
          <button
            key={option.id}
            className={`popover-option ${option.disabled ? 'disabled' : ''}`}
            onClick={() => handleOptionClick(option)}
            disabled={option.disabled}
            type="button"
          >
            <Typography variant="body">
              {option.label}
            </Typography>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Popover;
